import { useCallback, useEffect, useRef, useState } from 'react';
import { WaveFile } from 'wavefile';

interface UseVoiceRecordingReturn {
    isRecording: boolean;
    recordingLength: number;
    audioURL: string | null;
    audioBlob: Blob | null;
    startRecording: () => void;
    stopRecording: () => void;
    cancelRecording: () => void;
    clearRecordingWithoutRevoke: () => void;
    hasRecording: boolean;
    audioLevels: number[];
    /** The browser refused the microphone (D-CHAT-08/S06). */
    micBlocked: boolean;
    dismissMicBlocked: () => void;
}

/** A voice message stops recording at 5:00 (D-CHAT-08/S02). */
export const MAX_RECORDING_SECONDS = 300;

export const useVoiceRecording = (): UseVoiceRecordingReturn => {
    const [isRecording, setIsRecording] = useState(false);
    const [audioURL, setAudioURL] = useState<string | null>(null);
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
    const [recordingLength, setRecordingLength] = useState(0);
    const [audioLevels, setAudioLevels] = useState<number[]>([]);
    const [micBlocked, setMicBlocked] = useState(false);

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const recordingIntervalRef = useRef<number | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const audioHistoryRef = useRef<number[]>([]);
    const frameCountRef = useRef<number>(0);
    const isCancelledRef = useRef<boolean>(false);
    const skipNextAudioUrlRevokeRef = useRef<boolean>(false);

    useEffect(() => {
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
            if (recordingIntervalRef.current !== null) {
                clearInterval(recordingIntervalRef.current);
            }
            if (animationFrameRef.current !== null) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
            if (audioURL) {
                if (skipNextAudioUrlRevokeRef.current) {
                    skipNextAudioUrlRevokeRef.current = false;
                } else {
                    URL.revokeObjectURL(audioURL);
                }
            }
        };
    }, [audioURL]);

    const updateAudioLevels = useCallback(() => {
        if (!analyserRef.current) return;

        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);

        const average = dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length;
        const normalizedLevel = Math.min(1, (average / 255) * 2.5);

        audioHistoryRef.current.push(normalizedLevel);

        const maxBars = 150;
        if (audioHistoryRef.current.length > maxBars) {
            audioHistoryRef.current.shift();
        }

        frameCountRef.current++;
        if (frameCountRef.current % 3 === 0) {
            setAudioLevels([...audioHistoryRef.current]);
        }

        animationFrameRef.current = requestAnimationFrame(updateAudioLevels);
    }, []);

    const convertWebMToWav = (audioBlob: Blob): Promise<Blob> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const audioBuffer = reader.result as ArrayBuffer;
                const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
                audioCtx.decodeAudioData(audioBuffer, (buffer) => {
                    const wav = new WaveFile();
                    const samples = buffer.getChannelData(0);
                    const formattedSamples = new Int16Array(samples.length);
                    for (let i = 0; i < samples.length; i++) {
                        formattedSamples[i] = samples[i] * 0x7FFF;
                    }
                    wav.fromScratch(1, buffer.sampleRate, '16', formattedSamples);
                    const wavBlob = new Blob([wav.toBuffer()], { type: 'audio/wav' });
                    resolve(wavBlob);
                }, reject);
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(audioBlob);
        });
    };

    const startRecording = async () => {
        try {
            if (audioURL) {
                URL.revokeObjectURL(audioURL);
            }
            setAudioURL(null);
            setAudioBlob(null);
            isCancelledRef.current = false;

            setMicBlocked(false);
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            audioContextRef.current = audioContext;

            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 512;
            analyser.smoothingTimeConstant = 0.7;
            analyserRef.current = analyser;

            const source = audioContext.createMediaStreamSource(stream);
            source.connect(analyser);

            const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
            mediaRecorderRef.current = mediaRecorder;

            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = event => {
                audioChunksRef.current.push(event.data);
            };

            mediaRecorder.onstop = async () => {
                if (isCancelledRef.current) {
                    isCancelledRef.current = false;
                    return;
                }

                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                try {
                    const wavBlob = await convertWebMToWav(audioBlob);
                    const audioUrl = URL.createObjectURL(wavBlob);
                    setAudioBlob(wavBlob);
                    setAudioURL(audioUrl);
                } catch (error) {
                    console.error('Error converting audio:', error);
                }

                audioChunksRef.current = [];
                if (recordingIntervalRef.current !== null) {
                    clearInterval(recordingIntervalRef.current);
                    recordingIntervalRef.current = null;
                }

                if (animationFrameRef.current !== null) {
                    cancelAnimationFrame(animationFrameRef.current);
                    animationFrameRef.current = null;
                }

                if (audioContextRef.current) {
                    audioContextRef.current.close();
                    audioContextRef.current = null;
                }

                if (streamRef.current) {
                    streamRef.current.getTracks().forEach(track => track.stop());
                    streamRef.current = null;
                }
            };

            mediaRecorder.start();
            setIsRecording(true);
            setRecordingLength(0);
            audioHistoryRef.current = [];
            frameCountRef.current = 0;

            recordingIntervalRef.current = window.setInterval(() => {
                setRecordingLength(prevLength => prevLength + 1);
            }, 1000);

            updateAudioLevels();
        } catch (error) {
            console.error('Error accessing microphone:', error);
            // Denied or blocked for the site: say so in the composer instead
            // of an alert, with how to allow it (D-CHAT-08/S06).
            setMicBlocked(true);
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
            setIsRecording(false);

        }
        document.getElementById('mention-text-input')?.focus();
    };

    const cancelRecording = () => {
        isCancelledRef.current = true;

        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }

        if (recordingIntervalRef.current !== null) {
            clearInterval(recordingIntervalRef.current);
            recordingIntervalRef.current = null;
        }

        if (animationFrameRef.current !== null) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        if (audioURL) {
            URL.revokeObjectURL(audioURL);
        }

        setIsRecording(false);
        setAudioURL(null);
        setAudioBlob(null);
        setRecordingLength(0);
        setAudioLevels([]);
        audioHistoryRef.current = [];
        audioChunksRef.current = [];
        document.getElementById('mention-text-input')?.focus();
    };

    const clearRecordingWithoutRevoke = () => {
        if (audioURL) {
            skipNextAudioUrlRevokeRef.current = true;
        }
        setAudioURL(null);
        setAudioBlob(null);
        setRecordingLength(0);
        setAudioLevels([]);
        audioHistoryRef.current = [];
    };

    useEffect(() => {
        if (isRecording && recordingLength >= MAX_RECORDING_SECONDS) stopRecording();
        // stopRecording is recreated each render; the length is what matters.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRecording, recordingLength]);

    return {
        isRecording,
        recordingLength,
        audioURL,
        audioBlob,
        startRecording,
        stopRecording,
        cancelRecording,
        clearRecordingWithoutRevoke,
        hasRecording: !!audioBlob,
        audioLevels,
        micBlocked,
        dismissMicBlocked: () => setMicBlocked(false),
    };
};
